import { logRBIEvent } from 'features/analytics';
import { CustomEventNames, EventTypes } from 'features/analytics/constants';
import { buildLoyaltyEventAttributes } from 'features/loyalty/utils/analytics';

import {
  AppOffer,
  AppOfferGroup,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  LoyaltyOffer,
  appOfferToLoyaltyOffer,
  isAppOffer,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { blockContentToPlainText } from 'utils/sanity';

export function parseOfferListPosition(offerListPositionParam: unknown): number | null {
  const raw = Array.isArray(offerListPositionParam)
    ? offerListPositionParam[0]
    : offerListPositionParam;
  const parsed = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;

  if (Number.isInteger(parsed) && parsed >= 1) {
    return parsed;
  }

  return null;
}

/**
 * Amplitude's SDK treats `null` and `undefined` as invalid event properties and
 * drops the key before the inspector or ingestion sees it. Empty string is kept.
 */
export function toAmplitudeOfferListPosition(position: number | null): number | '' {
  return position ?? '';
}

export type OfferListPositionTarget =
  | { kind: 'offer'; loyaltyEngineId: string }
  | { kind: 'group'; cmsId: string; id?: string };

export function findOfferListItemIndex(
  items: AppOfferListItem[],
  target: OfferListPositionTarget
): number {
  return items.findIndex(item => {
    if (target.kind === 'offer') {
      return isAppOffer(item) && item.loyaltyEngineId === target.loyaltyEngineId;
    }

    if (target.id) {
      return isAppPersonalizedOfferGroup(item) && String(item.id) === String(target.id);
    }

    return (
      (isAppOfferGroup(item) || isAppPersonalizedOfferGroup(item)) &&
      String(item.cmsId) === String(target.cmsId)
    );
  });
}

export function offerListPositionParams(index: number): { olp: string } | Record<string, never> {
  return index >= 0 ? { olp: String(index + 1) } : {};
}

export type OffersDisplayedGroupAnalytics = {
  canOnlyChooseOne: boolean;
  childOfferCmsIds: string[];
  childOfferIds: string[];
  cmsId: string;
  groupType: 'AppOfferGroup' | 'AppPersonalizedOfferGroup';
  loyaltyEngineId: string;
  name: string;
  type: string;
  visibleOfferCount: number;
};

type OffersDisplayedAnalytics = {
  attributes: {
    offerCount: number;
    offerGroupCount: number;
    offerGroups: OffersDisplayedGroupAnalytics[];
    visibleItemCount: number;
  };
  standaloneOffers: LoyaltyOffer[];
};

function getItemId(item: unknown, keys: string[]): string {
  const source = item as Record<string, unknown>;
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return '';
}

function getGroupName(group: AppOfferGroup | AppPersonalizedOfferGroup): string {
  return blockContentToPlainText(group.name?.localeRaw);
}

function getChildOfferIds(offers: AppOffer[], keys: string[]): string[] {
  return offers.map(offer => getItemId(offer, keys)).filter(Boolean);
}

function mapOfferGroup(
  group: AppOfferGroup | AppPersonalizedOfferGroup
): OffersDisplayedGroupAnalytics {
  const childOfferIds = getChildOfferIds(group.offers, ['loyaltyEngineId', 'id']);
  const childOfferCmsIds = getChildOfferIds(group.offers, ['cmsId', '_id', 'sanityId']);
  const groupType = isAppPersonalizedOfferGroup(group)
    ? 'AppPersonalizedOfferGroup'
    : 'AppOfferGroup';

  return {
    canOnlyChooseOne: Boolean(group.canOnlyChooseOne),
    childOfferCmsIds,
    childOfferIds,
    cmsId: getItemId(group, ['cmsId', '_id', 'sanityId']),
    groupType,
    loyaltyEngineId: getItemId(group, ['loyaltyEngineId', 'id']),
    name: getGroupName(group),
    type: group.__typename ?? groupType,
    visibleOfferCount: group.offers.length,
  };
}

export function buildOffersDisplayedAnalytics(
  offerListItems: AppOfferListItem[]
): OffersDisplayedAnalytics {
  const standaloneOffers = offerListItems.filter(isAppOffer).map(appOfferToLoyaltyOffer);
  const offerGroups = offerListItems
    .filter((item): item is AppOfferGroup | AppPersonalizedOfferGroup => {
      return isAppOfferGroup(item) || isAppPersonalizedOfferGroup(item);
    })
    .map(mapOfferGroup);

  return {
    standaloneOffers,
    attributes: {
      offerCount: standaloneOffers.length,
      offerGroupCount: offerGroups.length,
      offerGroups,
      visibleItemCount: offerListItems.length,
    },
  };
}

export type OfferEvent = {
  name:
    | CustomEventNames.OFFER_SELECTED
    | CustomEventNames.OFFER_DETAILS_VIEWED
    | CustomEventNames.PROMO_CODE_APPLIED
    | CustomEventNames.OFFERS_DISPLAYED;
  incentives: LoyaltyOffer[];
  attributes?: Record<string, any>;
};

export function logOfferEvent(
  { name, incentives, attributes }: OfferEvent,
  logOptions?: { flushBrazeAfterLog?: boolean; skipLoggingToBraze?: boolean }
) {
  logRBIEvent(
    {
      name,
      type: EventTypes.Other,
      attributes: buildLoyaltyEventAttributes(incentives, attributes),
    },
    logOptions
  );
}

export function logOffersDisplayed(offerListItems: AppOfferListItem[]) {
  const { standaloneOffers, attributes } = buildOffersDisplayedAnalytics(offerListItems);
  logOfferEvent(
    { name: CustomEventNames.OFFERS_DISPLAYED, incentives: standaloneOffers, attributes },
    { skipLoggingToBraze: true }
  );
}
